import { GridState } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import loglevel from "loglevel";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "shared/components/toast/useToast";
import {
  compressString,
  decompressString,
} from "shared/utils/CompressionUtils";
import { StringParam, useQueryParam } from "use-query-params";

const DEFAULT_PARAM_NAME = "grid";

const log = loglevel.getLogger("MaybernTableGridState");

export const useExportGridStateUrl = (
  agGrid: AgGridReact | null,
  paramName: string = DEFAULT_PARAM_NAME,
) => {
  const { showToast } = useToast();
  const { t } = useTranslation(["common"]);

  return useCallback(async () => {
    const state = agGrid?.api.getState();
    const str = await compressString(JSON.stringify(state));

    const url = new URL(window.location.toString());
    url.searchParams.set(paramName, str);

    await navigator.clipboard.writeText(url.toString());

    showToast({
      message: t("common:linkCopiedToClipboard"),
      variant: "success",
    });
  }, [agGrid?.api, paramName, showToast, t]);
};

export const useGridStateParam = (paramName: string = DEFAULT_PARAM_NAME) => {
  const [gridStateParam, setGridStateParam] = useQueryParam(
    paramName,
    StringParam,
  );
  const [initialGridState] = useState(gridStateParam);
  const { data } = useSuspenseQuery<GridState | null>({
    queryKey: ["gridState", initialGridState],
    queryFn: async () => {
      if (initialGridState) {
        try {
          const str = await decompressString(initialGridState);
          return JSON.parse(str);
        } catch (e) {
          log.error(e);
        }
      }
      return null;
    },
  });
  useEffect(() => {
    if (gridStateParam) {
      setGridStateParam(null);
    }
  }, [setGridStateParam, gridStateParam]);
  return data ?? undefined;
};
